import {
  changePassword,
  deleteAccount,
  getAccountSettings,
  getAvatarUploadUrl,
  updateAccountSettings,
  uploadToS3,
} from '@/services/api/account-settings';
import { useAuthStore } from '@/store';
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  DeleteAccountRequest,
  DeleteAccountResponse,
  GetAccountSettingsResponse,
  GetAvatarUploadUrlResponse,
  UpdateAccountSettingsRequest,
  UpdateAccountSettingsResponse,
} from '@/types/api/account-settings';
import type { ApiErrorResponse } from '@/types/api/common';
import { useMutation, useQuery } from '@tanstack/react-query';

interface AccountSettingsOptions {
  loadGetAccountSettings?: boolean;
}

export const useAccountSettings = (options: AccountSettingsOptions = {}) => {
  const user = useAuthStore((state) => state.user);

  const { loadGetAccountSettings = false } = options;
  const getAccountSettingsApi = useQuery<
    GetAccountSettingsResponse,
    ApiErrorResponse
  >({
    queryKey: ['account-settings', user?.id],
    queryFn: () => getAccountSettings(),
    enabled: loadGetAccountSettings && !!user?.id,
  });

  const updateAccountSettingsApi = useMutation<
    UpdateAccountSettingsResponse,
    ApiErrorResponse,
    UpdateAccountSettingsRequest
  >({
    mutationFn: (payload) => updateAccountSettings(payload),
    onSuccess: () => {
      // Invalidate and refetch account settings after update
      getAccountSettingsApi.refetch();
    },
  });

  const deleteAccountApi = useMutation<
    DeleteAccountResponse,
    ApiErrorResponse,
    DeleteAccountRequest
  >({
    mutationFn: (payload) => deleteAccount(payload),
  });

  const changePasswordApi = useMutation<
    ChangePasswordResponse,
    ApiErrorResponse,
    ChangePasswordRequest
  >({
    mutationFn: (payload) => changePassword(payload),
  });

  const getAvatarUploadUrlApi = useMutation<
    GetAvatarUploadUrlResponse,
    ApiErrorResponse,
    string
  >({
    mutationFn: (fileType) => getAvatarUploadUrl(fileType),
  });

  const uploadAvatarApi = useMutation<
    void,
    ApiErrorResponse,
    { url: string; file: File }
  >({
    mutationFn: ({ url, file }) => uploadToS3(url, file),
    onSuccess: () => {
      // Invalidate and refetch account settings after avatar upload
      getAccountSettingsApi.refetch();
    },
  });

  return {
    getAccountSettingsApi,
    updateAccountSettingsApi,
    deleteAccountApi,
    changePasswordApi,
    getAvatarUploadUrlApi,
    uploadAvatarApi,
  };
};
